import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useQuote } from '../context/QuoteContext';
import { saveQuoteRequest } from '../services/database';

interface WorkDescriptionFormProps {
  onNext: () => void;
  onBack: () => void;
}

const WorkDescriptionForm: React.FC<WorkDescriptionFormProps> = ({ onNext, onBack }) => {
  const { dispatch } = useQuote();
  const [description, setDescription] = useState('');
  const [timeframe, setTimeframe] = useState('next-few-weeks');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeframes = [
    { id: 'immediately', label: 'As soon as possible' },
    { id: 'next-few-weeks', label: 'Next couple of weeks' },
    { id: 'next-few-months', label: 'Next few months' },
    { id: 'no-preference', label: 'No time preference' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!description.trim()) {
      setError('Please describe the work you need help with');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const savedRequest = await saveQuoteRequest(description, 'general', timeframe);
      
      dispatch({ 
        type: 'SET_WORK_DESCRIPTION', 
        payload: { 
          id: savedRequest?.id,
          description,
          category: 'general',
          timeframe
        } 
      });
      
      onNext();
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-fadeIn">
      <form onSubmit={handleSubmit}>
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-2">Tell us about your project</h2>
          <p className="text-gray-600 mb-6">
            Describe what you need done, and we'll ask any clarifying questions in the next step.
          </p>
          
          <textarea
            id="description"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all min-h-[120px]"
            placeholder="E.g., I need to repair a leaking kitchen faucet that drips constantly, or I want to remodel my bathroom..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>
        
        <div className="mb-8">
          <label htmlFor="timeframe" className="block text-sm font-medium text-gray-700 mb-2">
            When would you like the work completed?
          </label>
          <select
            id="timeframe"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
          >
            {timeframes.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        
        {error && (
          <div className="mb-6 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        
        <div className="flex flex-col gap-3">
          <button
            type="submit"
            className="w-full bg-black text-white px-4 py-3 rounded-lg font-medium transition-colors duration-300 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? 'Analyzing...' : 'Get Price Estimate'}
          </button>
          
          <button 
            type="button"
            onClick={onBack}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default WorkDescriptionForm